<script>
  import Section from '../components/Section.svelte';
  import Panel from '../components/Panel.svelte';
  import Button from '../components/Button.svelte';
  import Stepper from '../components/Stepper.svelte';
  import Async from '../components/Async.svelte';
  import HelpDrawer from '../components/HelpDrawer.svelte';
  import { draft } from '../lib/partnerDraft.svelte.js';
  import { getConfig, getDatasets } from '../lib/api.js';
  import { datasetGroups, sharingWords } from '../lib/slices.js';
  import { HELP } from '../lib/help.js';

  const steps = ['Partner details', 'What you share', 'Review'];
  // aggregations.json read live from the Pod, each cube with its dataset; the
  // datasets offered here are the ones that have cubes.
  const groupsPromise = Promise.all([getConfig('aggregations'), getDatasets()])
    .then(([aggregations, { datasets }]) => datasetGroups(aggregations, datasets));
</script>

<HelpDrawer {...HELP['partner/access']} />

<h1 class="wf2-title wf2-title-level-1">Add sharing partner</h1>
<Stepper {steps} active={1} />

<Section title="What you share with them (optional)">
  <Panel>
    <p class="wf2-text">
      Pick the datasets to share, then continue to review. You share the counts
      of whole <strong>datasets</strong>, never individual records: each dataset's
      cubes travel together, with privacy rules for when small counts are left
      out. Choose what
      <strong>{draft.name || 'this partner'}</strong> gets.
    </p>

    <Async promise={groupsPromise} loading="Reading datasets from your Pod…">
      {#snippet children(groups)}
    {#each groups as group}
      <div class="wf2-panel" style="margin-top: var(--space-3)">
        <div class="wf2-checkline">
          <input class="wf2-check" type="checkbox" id={group.id} bind:checked={draft.datasets[group.id]} />
          <label for={group.id}>
            <strong><span class="tag {group.id}">{group.label}</span></strong>
            — {group.rule},
            {group.sharing ? sharingWords(group.sharing) : 'sharing differs per cube'}
          </label>
        </div>
        <div class="wf2-tablewrap" style="overflow-x:auto">
          <table class="wf2-table">
            <thead><tr><th>Cube</th><th>Breaks down by</th><th>What it counts</th>{#if !group.sharing}<th>Sharing</th>{/if}</tr></thead>
            <tbody>
              {#each group.slices as s}
                <tr>
                  <td class="mono">{s.dataSet}</td>
                  <td class="mono">{s.dimensions.join(', ')}</td>
                  <td class="mono">{s.reducer} {s.measure}{#if s.suppressMeasureBelow !== null} · hide totals under {s.suppressMeasureBelow}{/if}</td>
                  {#if !group.sharing}<td>{sharingWords(s.sharing)}</td>{/if}
                </tr>
              {/each}
            </tbody>
          </table>
        </div>
      </div>
    {/each}
      {/snippet}
    </Async>

    <p class="wf2-hint">
      They get only the cubes listed under the datasets you select. Your private
      records and every unselected cube stay in your Pod.
      Select nothing if you only want to receive from this partner and give
      nothing back. See the “?”.
    </p>
  </Panel>
</Section>

<div class="wf2-row" style="gap:12px">
  <Button variant="primary" to="partner/confirm">Next: review</Button>
  <Button variant="secondary" to="partner">Back</Button>
</div>
